import { Flex, Menu, UnstyledButton, createStyles } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { trackEvent } from '@repo/common/analytics';
import { Icon, Switch, Text } from '@repo/foundations';
import { isNil } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { useCallback } from 'react';
import type { INotification } from '../../../../api/types/models/notification';
import { ANALYTICS_EVENTS } from '../../constants';
import { inboxFeatureStore } from '../../store';
import type { INotificationActionsProps } from '../../types';

const useStyles = createStyles((theme) => ({
	submenuDropdown: {
		width: theme.other.space[64],
		maxWidth: theme.other.space[64],
		padding: 0,
		marginLeft: 0,
	},
	menuRowButtonContainer: {
		width: '100%',
	},
	menuRowButton: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		width: '100%',
		height: theme.other.height.sm,
		padding: `0 ${theme.spacing['2xs']}`,
		borderRadius: theme.radius.sm,
		'&:hover': {
			backgroundColor: theme.other.getColor('fill/primary/hover'),
		},
		'&:active': {
			backgroundColor: theme.other.getColor('fill/primary/selected'),
		},
	},
}));

export const InboxListHeaderActions = observer(
	({
		selected,
		handleDeleteAll,
		handleDeleteAllRead,
		handleMarkAllAsRead,
		handleDelete,
		handleMarkAsRead,
		navigate,
		user,
		workspace,
		notifications,
		markAsRead,
		restoreAllNotifications,
	}: INotificationActionsProps) => {
		const { classes } = useStyles();

		const showUndoNotification = useCallback(
			(
				type: 'mark_all_read' | 'delete_all' | 'delete_all_read',
				undoFn: () => void,
				message: string
			) => {
				const undoAction = {
					type,
					data: { undoFn },
					timestamp: Date.now(),
				};
				inboxFeatureStore.setPendingUndoAction(undoAction);

				showNotification({
					message: `${message} (Press Ctrl+Z to undo)`,
					color: 'blue',
					autoClose: 8000,
					onClose: () => inboxFeatureStore.clearUndoAction(),
				});

				setTimeout(() => {
					inboxFeatureStore.clearUndoAction();
				}, 8000);
			},
			[]
		);

		const handleMarkAllAsReadWithUndo = useCallback(() => {
			const unreadNotificationIds = notifications
				.filter((notification: INotification) => !notification.read)
				.map((notification: INotification) => notification.id);

			const currentRecentlyRead = [...inboxFeatureStore.recentlyRead];

			handleMarkAllAsRead();

			showUndoNotification(
				'mark_all_read',
				async () => {
					inboxFeatureStore.recentlyRead = currentRecentlyRead;

					const results = await Promise.allSettled(
						unreadNotificationIds.map((notificationId) =>
							markAsRead({
								id: notificationId,
								status: false, // Mark as unread
							})
						)
					);

					const successfulResults = results.filter(
						(result) => result.status === 'fulfilled'
					);
					const failedResults = results.filter(
						(result) => result.status === 'rejected'
					);

					if (failedResults.length > 0) {
						showNotification({
							message: `Restored ${successfulResults.length} notifications as unread, ${failedResults.length} failed`,
							color: 'orange',
							autoClose: 3000,
						});
					} else {
						showNotification({
							message: `Restored ${successfulResults.length} notifications as unread`,
							color: 'green',
							autoClose: 3000,
						});
					}
				},
				'Marked all notifications as read'
			);
		}, [handleMarkAllAsRead, showUndoNotification, notifications, markAsRead]);

		const handleDeleteAllWithUndo = useCallback(() => {
			// IDs that will be deleted
			const notificationIds = notifications.map((n: INotification) => n.id);

			handleDeleteAll();

			showUndoNotification(
				'delete_all',
				async () => {
					try {
						await restoreAllNotifications({ notificationIds });
					} catch (e) {
						showNotification({
							message: 'Failed to restore notifications',
							color: 'red',
						});
					}
				},
				`Deleted ${notificationIds.length} notifications`
			);
		}, [
			handleDeleteAll,
			showUndoNotification,
			notifications,
			restoreAllNotifications,
		]);
		const handleDeleteAllReadWithUndo = useCallback(() => {
			const readNotificationIds = notifications
				.filter((n: INotification) => n.read)
				.map((n) => n.id);

			handleDeleteAllRead();

			showUndoNotification(
				'delete_all_read',
				async () => {
					try {
						await restoreAllNotifications({
							notificationIds: readNotificationIds,
						});
					} catch (e) {
						showNotification({
							message: 'Failed to restore notifications',
							color: 'red',
						});
					}
				},
				`Deleted ${readNotificationIds.length} read notifications`
			);
		}, [
			handleDeleteAllRead,
			showUndoNotification,
			notifications,
			restoreAllNotifications,
		]);

		const handleShowReadToggle = useCallback(() => {
			trackEvent(
				ANALYTICS_EVENTS.SHOW_READ_TOGGLE,
				{
					action: inboxFeatureStore.showRead ? 'hide_read' : 'show_read',
					currentValue: inboxFeatureStore.showRead.toString(),
				},
				user,
				workspace
			);

			inboxFeatureStore.toggleShowRead();
		}, [user, workspace]);

		const handleSettingsNavigate = useCallback(() => {
			trackEvent(
				ANALYTICS_EVENTS.SETTINGS_NAVIGATE,
				{
					destination: '/settings/notifications/app',
					fromContext: 'inbox_header_menu',
				},
				user,
				workspace
			);

			navigate('/settings/notifications/app');
		}, [navigate, user, workspace]);

		const handleMarkAsReadClick = useCallback(
			(id: string) => {
				const notification = notifications.find((n) => n.id === id);
				trackEvent(
					ANALYTICS_EVENTS.NOTIFICATION_MARK_READ,
					{
						notificationId: id,
						notificationEvent: notification?.event || 'unknown',
						resourceType: notification?.resource_type || 'unknown',
						action: notification?.read ? 'mark_unread' : 'mark_read',
						fromContext: 'header_menu',
					},
					user,
					workspace
				);

				handleMarkAsRead({ id });
			},
			[handleMarkAsRead, notifications, user, workspace]
		);

		const handleDeleteClick = useCallback(
			(id: string) => {
				const notification = notifications.find((n) => n.id === id);
				trackEvent(
					ANALYTICS_EVENTS.NOTIFICATION_DELETE,
					{
						notificationId: id,
						notificationEvent: notification?.event || 'unknown',
						resourceType: notification?.resource_type || 'unknown',
						fromContext: 'header_menu',
					},
					user,
					workspace
				);

				handleDelete({ id });
			},
			[handleDelete, notifications, user, workspace]
		);

		return (
			<>
				{!isNil(selected) && (
					<>
						<Menu.Item
							icon={
								selected?.read ? (
									<Icon name="notification" />
								) : (
									<Icon name="squareCheck" />
								)
							}
							onClick={() => handleMarkAsReadClick(selected.id)}
						>
							{selected?.read ? 'Mark as unread' : 'Mark as read'}
						</Menu.Item>
						<Menu.Item
							icon={<Icon name="trash" />}
							onClick={() => handleDeleteClick(selected.id)}
						>
							Delete notification
						</Menu.Item>
						<Menu.Divider />
					</>
				)}
				<Menu.Item
					icon={<Icon name="check" />}
					onClick={handleMarkAllAsReadWithUndo}
				>
					Mark all as read
				</Menu.Item>
				<Menu.Item
					closeMenuOnClick={false}
					onClick={handleShowReadToggle}
					icon={<Icon name="checkbox" />}
					rightSection={
						<Switch
							size="sm"
							checked={inboxFeatureStore.showRead}
							onChange={handleShowReadToggle}
						/>
					}
					data-testid="inbox-view-menu-show-read-switch-jHDjYXvm5q"
				>
					Show read
				</Menu.Item>
				<Menu
					classNames={{ dropdown: classes.submenuDropdown }}
					offset={{ mainAxis: 10 }}
					position="right-start"
					shadow="md"
					transitionProps={{ transition: 'fade' }}
					trigger="hover"
				>
					<Menu.Target>
						<Flex className={classes.menuRowButtonContainer}>
							<UnstyledButton className={classes.menuRowButton}>
								<Flex align="center" gap="xs">
									<Icon name="trash" />
									<Text size="sm">Delete...</Text>
								</Flex>
								<Icon name="chevronRight" color="icon/secondary/default" />
							</UnstyledButton>
						</Flex>
					</Menu.Target>
					<Menu.Dropdown p="3xs">
						<Menu.Item onClick={handleDeleteAllWithUndo}>
							Delete all notifications...
						</Menu.Item>
						<Menu.Item onClick={handleDeleteAllReadWithUndo}>
							Delete all read notifications...
						</Menu.Item>
					</Menu.Dropdown>
				</Menu>
				<Menu.Divider />
				<Menu.Item
					onClick={handleSettingsNavigate}
					icon={<Icon name="settings" />}
				>
					Notification Settings
				</Menu.Item>
			</>
		);
	}
);
